/**
 * Mobile-only document view/download (Capacitor iOS/Android).
 *
 * View + download both use auth blob proxy (/api/storage/download/*) — no presigned
 * fetch in WebView (avoids R2 CORS and presign failures). Images/PDFs render from
 * blob URLs; download writes cache + File Opener.
 *
 * Web/desktop stays on storage.js (view = new tab, download unchanged).
 */
import {
  normalizeDocRef,
  resolveDocumentUrl,
  fetchPresignedDocUrl,
  resolveStorageBlobPath,
  fetchAuthenticatedDocBlob,
} from '../storage';

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      resolve(String(dataUrl).split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function mimeFromName(name = '') {
  const ext = String(name).split('.').pop()?.toLowerCase();
  const map = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
    csv: 'text/csv',
  };
  return map[ext] || 'application/octet-stream';
}

function safeDownloadName(name = 'document') {
  return String(name).replace(/[^\w.\-()+ ]+/g, '_').slice(0, 180) || 'document';
}

async function inlineToBlob(ref) {
  const inline = ref.url || ref.dataUrl || ref.data_url || ref.base64 || ref.file;
  if (!inline) return null;
  const s = String(inline);
  if (s.startsWith('data:') || s.startsWith('blob:')) {
    const res = await fetch(s);
    return res.blob();
  }
  return null;
}

async function resolveBlobPath(docObj, fallbackName, ctx = {}) {
  const ref = normalizeDocRef(docObj, fallbackName);
  if (!ref) throw new Error('Could not open document');
  const blobPath = await resolveStorageBlobPath(ref, ctx);
  if (!blobPath) throw new Error('Document file path not found');
  return { ref, blobPath };
}

/** Blob bytes for mobile preview and download (auth proxy avoids R2 CORS). */
export async function resolveMobileDocumentBlob(docObj, fallbackName, ctx = {}) {
  const ref = normalizeDocRef(docObj, fallbackName);
  if (!ref) throw new Error('Could not open document');

  const inlineBlob = await inlineToBlob(ref);
  if (inlineBlob) {
    return { blob: inlineBlob, name: ref.name || fallbackName || 'document' };
  }

  const { blobPath } = await resolveBlobPath(docObj, fallbackName, ctx);
  const hit = await fetchAuthenticatedDocBlob(blobPath);
  const blob = hit?.blob || hit;
  return { blob, name: ref.name || fallbackName || 'document' };
}

/** Object URL for in-app preview (caller must revoke). */
export async function resolveMobileViewBlobUrl(docObj, fallbackName, ctx = {}) {
  const { blob } = await resolveMobileDocumentBlob(docObj, fallbackName, ctx);
  return URL.createObjectURL(blob);
}

/** @deprecated Use resolveMobileViewBlobUrl — kept for callers that still presign. */
export async function resolveMobilePresignedViewUrl(docObj, fallbackName, ctx = {}) {
  return resolveMobileViewBlobUrl(docObj, fallbackName, ctx);
}

/** @deprecated Use resolveMobileDocumentBlob */
export async function resolveMobilePdfBlobUrl(docObj, fallbackName, ctx = {}) {
  return resolveMobileViewBlobUrl(docObj, fallbackName, ctx);
}

/** Presigned GET URL with Content-Disposition (web fallback only). */
export async function resolveMobilePresignedDownloadUrl(docObj, fallbackName, ctx = {}) {
  const ref = normalizeDocRef(docObj, fallbackName);
  if (!ref) throw new Error('Could not download document');

  const inline = ref.url || ref.dataUrl || ref.data_url || ref.base64 || ref.file;
  if (inline && (String(inline).startsWith('data:') || String(inline).startsWith('blob:'))) {
    return inline;
  }

  const { blobPath } = await resolveBlobPath(docObj, fallbackName, ctx);
  const name = ref.name || fallbackName || 'document';
  try {
    return await fetchPresignedDocUrl(blobPath, name);
  } catch {
    return resolveDocumentUrl({ ...ref, path: blobPath }, { download: true });
  }
}

export async function viewDocumentMobile(docObj, fallbackName, ctx = {}) {
  await resolveMobileDocumentBlob(docObj, fallbackName, ctx);
  window.dispatchEvent(new CustomEvent('fb-view-document', {
    detail: { doc: docObj, name: fallbackName, ...ctx },
  }));
}

export async function downloadDocumentMobile(docObj, fallbackName, ctx = {}) {
  const { blob, name } = await resolveMobileDocumentBlob(docObj, fallbackName, ctx);
  const fileName = safeDownloadName(name);
  const base64 = await blobToBase64(blob);
  const mime = mimeFromName(fileName);

  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { FileOpener } = await import('@capacitor-community/file-opener');

  const written = await Filesystem.writeFile({
    path: fileName,
    data: base64,
    directory: Directory.Cache,
  });

  await FileOpener.open({
    filePath: written.uri,
    contentType: mime,
    openWithDefault: true,
  });

  return written.uri;
}
